import React from 'react'
import { useTranslations } from 'next-intl'
import { Controller, useFormContext } from 'react-hook-form'
import { Box } from '@alto-avios/alto-ui'
import classNames from 'classnames'
import { HotelResultsFormState } from '@reward-platform/ancillaries-schemas/hotel'
import { DatePicker } from '@reward-platform/lift/components'
import { useLocalisation } from '~/context/localisation'
import { isClient } from '~/utils/envChecks'
import DestinationAutoComplete from '~/components/search-and-results/SearchHotelForm/DestinationAutoComplete'
import { TravelerRoomPicker } from '../TravelerRoomPicker'
import { EditSearchResultsFormProps } from './EditSearchResultsForm.types'
import styles from './EditSearchResultsForm.module.scss'

function EditSearchResultsForm({
  dates,
  destination,
  variant,
  submitForm,
  getFieldError,
  getChildAgeError,
  getDestinationError,
  popularDestinations,
  showRecentSearches = false,
  formButton,
}: EditSearchResultsFormProps) {
  const t = useTranslations()
  const { locale } = useLocalisation()
  const showPopularDestinations = popularDestinations && popularDestinations.length > 0
  const { handleSubmit, control } = useFormContext<HotelResultsFormState>()

  const focusTravellersField = () => {
    if (isClient()) {
      const travellersInput =
        document.querySelector<HTMLInputElement>('#dropdown-traveller-input') ??
        document.querySelector<HTMLInputElement>('#dropdown-traveller-input input')
      travellersInput?.focus()
    }
  }

  return (
    <form
      className={classNames(styles.container, {
        [styles.inline]: variant === 'inline',
      })}
      autoComplete="off"
      noValidate
      onSubmit={handleSubmit(submitForm)}
    >
      <Controller
        control={control}
        name="destination"
        render={() => (
          <Box display="inline" className={styles.destinationField}>
            <DestinationAutoComplete
              destination={destination}
              showRecentSearches={showRecentSearches}
              error={getDestinationError()}
              {...(showPopularDestinations ? { popularDestinations } : {})}
            />
          </Box>
        )}
      />
      <Box display="inline" className={styles.datesField}>
        <DatePicker
          isRangePicker
          startLabel={t('pages.search.hotels.labels.check_in')}
          endLabel={t('pages.search.hotels.labels.check_out')}
          values={{ startDateTime: dates.startDate, endDateTime: dates.endDate || undefined }}
          onChange={(newDates) => {
            if (Array.isArray(newDates)) {
              const [startDate, endDate] = newDates
              dates.handleDateChange([startDate || dates.startDate, endDate || null])
            }
          }}
          minDate={dates.minDate}
          maxDate={dates.maxDate}
          maxEndDate={dates.maxEndDate}
          endDateSelectable
          locale={locale}
          errorStartDate={getFieldError?.('startDateTime')}
          errorEndDate={getFieldError?.('endDateTime')}
          onRangeCompleteForwardTab={focusTravellersField}
        />
      </Box>
      <Box display="inline" className={styles.travellersField}>
        <Controller
          name="rooms"
          control={control}
          render={({ field: { value: rooms, onChange } }) => (
            <TravelerRoomPicker
              rooms={rooms}
              onChange={onChange}
              getChildAgeError={getChildAgeError}
            />
          )}
        />
      </Box>
      <Box display="inline" className={styles.formButtonContainer}>
        {formButton}
      </Box>
    </form>
  )
}

export default EditSearchResultsForm
